<script setup>
/**
 * BotCard.vue
 *
 * A card component that displays a single bot with quick-access navigation,
 * a contextual "more" menu, and footer links to transcripts and analytics.
 *
 * @example
 * <BotCard :bot="bot" :workspaceId="workspaceId" @delete="handleDelete" />
 */

import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const router = useRouter();

const props = defineProps({
  /** The bot object — { _id, name, defaultLanguage, createdAt } */
  bot: {
    type: Object,
    required: true
  },
  /** The current workspace ID used for route params */
  workspaceId: {
    type: String,
    required: true
  },
  /** URL "Apri Live Chat" del bot (null se il connettore non è collegato) */
  liveChatHref: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['delete']);

// ── Menu ──────────────────────────────────────────────────────────────────────

const menuRef = ref(null);

const menuItems = computed(() => [
  {
    label: t('bots.botCard.deleteBot'),
    command: () => emit('delete', props.bot._id)
  }
]);

const toggleMenu = (event) => {
  menuRef.value.toggle(event);
};

// ── Quick access links ────────────────────────────────────────────────────────

const quickLinks = [
  { routeName: 'BotStorage',  labelKey: 'bots.botCard.documents', icon: 'description'    },
  { routeName: 'BotQA',       labelKey: 'bots.botCard.qa',        icon: 'question_answer' },
  { routeName: 'BotPrompting', labelKey: 'bots.botCard.prompt',   icon: 'edit_note'      },
  { routeName: 'BotSettings', labelKey: 'bots.botCard.settings',  icon: 'settings'       },
  { routeName: 'BotDeploy',   labelKey: 'bots.botCard.deploy',    icon: 'cloud_upload'   },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const languageFlags = { it: '🇮🇹', en: '🇬🇧', de: '🇩🇪', fr: '🇫🇷', es: '🇪🇸', pt: '🇵🇹' };
const languageFlag = computed(() => languageFlags[props.bot.defaultLanguage] || '🌐');

const formattedDate = computed(() => new Date(props.bot.createdAt).toLocaleDateString());

const saveCurrentBot = () => {
  try {
    localStorage.setItem('currentBot', JSON.stringify({ id: props.bot._id, name: props.bot.name }));
  } catch { /* ignore */ }
};

const navigateToDocuments = () => {
  saveCurrentBot();
  router.push({ name: 'BotStorage', params: { workspaceId: props.workspaceId, id: props.bot._id } });
};
</script>

<template>
  <!--
    Card wrapper: no explicit bg — each section defines its own background.
    rounded-lg overflow-hidden clips the children's backgrounds to the card shape.
  -->
  <div data-telemetry-id="bot-card" class="rounded-lg overflow-hidden flex flex-col shadow-lg">

    <!-- HEADER ───────────────────────────────────────────────────────────────── -->
    <div data-telemetry-id="bot-card-header" class="bg-[var(--p-surface-600)] p-4 flex items-start justify-between gap-2 cursor-pointer" @click="navigateToDocuments">

      <!-- Left: flag + name + date -->
      <div class="min-w-0">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-xl leading-none">{{ languageFlag }}</span>
          <h3 class="text-xl font-bold text-white truncate">{{ bot.name }}</h3>
        </div>
        <p class="text-xs text-[var(--p-surface-300)] mt-1">
          {{ $t('bots.list.createdOn') }} {{ formattedDate }}
        </p>
      </div>

      <!-- Right: "more" button + popup menu -->
      <div class="shrink-0">
        <Button
          data-telemetry-id="btn-bot-card-more"
          severity="secondary"
          text
          aria-haspopup="true"
          class="group"
          @click.stop="toggleMenu"
        >
          <span class="material-icons text-[var(--p-surface-300)] group-hover:text-white transition-colors">more_vert</span>
        </Button>

        <Menu ref="menuRef" :model="menuItems" :popup="true">
          <template #item="{ item, props: itemProps }">
            <a v-bind="itemProps.action" data-telemetry-id="bot-card-menu-item" class="flex items-center gap-2 text-red-400">
              <span class="material-icons text-sm">delete</span>
              <span>{{ item.label }}</span>
            </a>
          </template>
        </Menu>
      </div>
    </div>

    <!-- BODY — Quick access ──────────────────────────────────────────────────── -->
    <div class="bg-[var(--p-surface-700)] pt-4 pb-6 flex-grow">
      <p class="text-xs text-[var(--p-surface-300)] mb-3 text-center select-none">
        {{ $t('bots.botCard.quickAccess') }}:
      </p>

      <div class="flex justify-center gap-3">
        <router-link
          v-for="link in quickLinks"
          :key="link.routeName"
          :to="{ name: link.routeName, params: { workspaceId, id: bot._id } }"
          class="group"
          @click="saveCurrentBot"
        >
          <Button data-telemetry-id="btn-bot-card-quick-link" severity="secondary" text v-tooltip.bottom="$t(link.labelKey)">
            <span class="material-icons text-2xl text-[var(--p-surface-300)] group-hover:text-[var(--p-primary-color)] transition-colors">{{ link.icon }}</span>
          </Button>
        </router-link>
      </div>
    </div>

    <!-- FOOTER ───────────────────────────────────────────────────────────────── -->
    <div class="bg-[var(--p-surface-700)] border-t border-[var(--p-surface-600)] flex">

      <!-- Transcripts -->
      <router-link
        :to="{ name: 'BotTranscripts', params: { workspaceId, id: bot._id } }"
        data-telemetry-id="btn-bot-card-transcripts"
        class="flex-1 flex items-center justify-center gap-2 py-3 text-sm text-[var(--p-surface-200)] hover:bg-[var(--p-surface-600)] transition-colors"
        @click="saveCurrentBot"
      >
        <span class="material-icons text-sm text-[var(--p-primary-color)]">format_list_bulleted</span>
        {{ $t('bots.botCard.transcripts') }}
      </router-link>

      <!-- Divider -->
      <div class="w-px bg-[var(--p-surface-500)]"></div>

      <!-- Analytics -->
      <router-link
        :to="{ name: 'BotAnalytics', params: { workspaceId, id: bot._id } }"
        data-telemetry-id="btn-bot-card-analytics"
        class="flex-1 flex items-center justify-center gap-2 py-3 text-sm text-[var(--p-surface-200)] hover:bg-[var(--p-surface-600)] transition-colors"
        @click="saveCurrentBot"
      >
        <span class="material-icons text-sm text-[var(--p-primary-color)]">show_chart</span>
        {{ $t('bots.botCard.analytics') }}
      </router-link>

      <!-- Live Chat (solo se il connettore è collegato a un team) -->
      <template v-if="liveChatHref">
        <div class="w-px bg-[var(--p-surface-500)]"></div>
        <a
          :href="liveChatHref"
          target="_blank"
          rel="noopener"
          data-telemetry-id="btn-bot-card-livechat"
          class="flex-1 flex items-center justify-center gap-2 py-3 text-sm text-[var(--p-surface-200)] hover:bg-[var(--p-surface-600)] transition-colors"
        >
          <span class="material-icons text-sm text-[var(--p-primary-color)]">support_agent</span>
          {{ $t('botEditor.connectors.livechat.open') }}
        </a>
      </template>

    </div>
  </div>
</template>
